import { useQuery } from '@apollo/client'
import { Brand, CustomFieldSet, Query } from '@promoboxx/graphql-gateway-types'
import { createContext, useContext, useMemo, useState } from 'react'

import useCurrentRetailerAccount from '@src/lib/useCurrentRetailerAccount'

import { GET_CUSTOM_FIELDS } from './operations'

export interface CustomFieldsContext {
  refetch: () => Promise<void>
  loading: boolean
  customFields: CustomFieldSet[]
  areAllRequiredFieldsFilled: boolean
  setBrandId: (brandId: Brand['id'] | null) => void
}

export const customFieldsContext = createContext<CustomFieldsContext>({
  refetch: async () => {},
  loading: false,
  customFields: [],
  areAllRequiredFieldsFilled: true,
  setBrandId: () => {},
})

export const CustomFieldsContextProvider: React.FC<React.PropsWithChildren> = (
  props,
) => {
  const { ensureCurrentRetailerAccount } = useCurrentRetailerAccount()
  const [brandId, setBrandId] = useState<Brand['id'] | null>(null)

  const { data, refetch, loading } = useQuery<Query>(GET_CUSTOM_FIELDS, {
    variables: {
      params: {
        resourceId: brandId,
        resourceType: 'brands',
      },
      fieldsParams: {
        resourceId: brandId,
        resourceType: 'brands',
      },
      fieldValueParams: {
        resourceId: ensureCurrentRetailerAccount.id,
        resourceType: 'retailer_accounts',
      },
    },
    skip: !(brandId && ensureCurrentRetailerAccount.id),
  })

  const value = useMemo(() => {
    const customFields = data?.getCustomFieldSets || []
    let numRequired = 0
    let numFilled = 0

    for (const fieldSet of customFields) {
      for (const field of fieldSet.customFields) {
        if (field.required) {
          numRequired++
          const defaultHasValue = Boolean(field.customFieldValues?.value)
          const hasValue =
            field.input_type === 'boolean'
              ? field.customFieldValues?.value === 'false'
                ? false
                : defaultHasValue
              : defaultHasValue

          if (hasValue) {
            numFilled++
          }
        }
      }
    }

    const value: CustomFieldsContext = {
      refetch: async () => {
        await refetch()
      },
      loading,
      customFields,
      areAllRequiredFieldsFilled: numRequired === numFilled,
      setBrandId,
    }

    return value
  }, [data?.getCustomFieldSets, loading, refetch])

  return (
    <customFieldsContext.Provider value={value}>
      {props.children}
    </customFieldsContext.Provider>
  )
}

export function useCustomFields() {
  return useContext(customFieldsContext)
}
